import { useRolodexEdit } from 'features/fde'
import { useRolodexEntries } from 'features/rolodex'
import { useCallback, useMemo, useState } from 'react'
import {
  createDefaultEntry,
  Form781AData,
  Form781AStatus,
  KVAnnotation,
  RolodexEntryType,
} from 'types'

interface UseAnnotationFormsOptions {
  kestrelModelId: string | undefined
  annotation: KVAnnotation
}

export function useAnnotationForms({
  kestrelModelId,
  annotation,
}: UseAnnotationFormsOptions) {
  const { rolodexPatch } = useRolodexEdit()

  // optimistically assume the checkbox works to prevent UI delays in form display
  const [optimisticToggles, setOptimisticToggles] = useState<Set<string>>(
    new Set()
  )

  const {
    entries: allForm781AEntries,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(
    kestrelModelId
      ? {
          type: RolodexEntryType.FORM_781A,
          tags: { 'kestrel-model': kestrelModelId },
        }
      : undefined
  )

  const inProgressForms = useMemo(() => {
    const sortByDate = (
      a: (typeof allForm781AEntries)[0],
      b: (typeof allForm781AEntries)[0]
    ) => {
      const dateA =
        (a.data.formData as Form781AData | undefined)?.createdAt ?? ''
      const dateB =
        (b.data.formData as Form781AData | undefined)?.createdAt ?? ''
      return dateB.localeCompare(dateA) // Newest first
    }

    return allForm781AEntries
      .filter(
        (entry) =>
          (entry.data.formData as Form781AData | undefined)?.status ===
          Form781AStatus.InProgress
      )
      .sort(sortByDate)
  }, [allForm781AEntries])

  const isFormChecked = useCallback(
    (formId: string) => {
      const form = allForm781AEntries.find((e) => e.id === formId)
      if (!form) return false

      const formData = form.data.formData as Form781AData | undefined
      if (!formData) return false

      const actuallyChecked = formData.entries.some(
        (entry) => entry.annotationId === annotation.id
      )

      const isOptimistic = optimisticToggles.has(formId)
      return isOptimistic ? !actuallyChecked : actuallyChecked
    },
    [allForm781AEntries, annotation.id, optimisticToggles]
  )

  const toggleFormSelection = useCallback(
    async (formId: string) => {
      const removeOptimisticToggle = () => {
        setOptimisticToggles((prev) => {
          const next = new Set(prev)
          next.delete(formId)
          return next
        })
      }

      setOptimisticToggles((prev) => {
        const next = new Set(prev)
        next.add(formId)
        return next
      })

      const form = allForm781AEntries.find((e) => e.id === formId)
      if (!form) {
        removeOptimisticToggle()
        return
      }

      const formData = (form.data.formData as Form781AData | undefined) ?? {
        name: 'Untitled Form',
        status: Form781AStatus.InProgress,
        createdAt: new Date().toISOString(),
        header: { from: '', to: '', mds: '', serialNumber: '' },
        entries: [],
      }

      const excludedAnnotations =
        (form.data.excludedAnnotations as string[]) || []
      const isCurrentlyChecked = formData.entries.some(
        (entry) => entry.annotationId === annotation.id
      )

      let updatedFormData: Form781AData
      let updatedExcluded: string[]

      if (isCurrentlyChecked) {
        // Remove annotation from form
        updatedFormData = {
          ...formData,
          entries: formData.entries.filter(
            (entry) => entry.annotationId !== annotation.id
          ),
        }
        updatedExcluded = [...excludedAnnotations, annotation.id]
      } else {
        // Add annotation to form
        const discrepancyText = [
          `Severity: ${annotation.severity || 'N/A'}`,
          `Damage Type: ${annotation.type || 'N/A'}`,
          `Notes: ${annotation.notes || 'N/A'}`,
        ].join('\n')

        const newEntry = createDefaultEntry(
          annotation.id,
          annotation.label || null,
          annotation.tags?.inspectionArea?.value ?? null
        )
        newEntry.discrepancyText = discrepancyText

        updatedFormData = {
          ...formData,
          entries: [...formData.entries, newEntry],
        }
        updatedExcluded = excludedAnnotations.filter(
          (id) => id !== annotation.id
        )
      }

      try {
        await rolodexPatch([
          {
            id: formId,
            data: {
              formData: updatedFormData,
              excludedAnnotations: updatedExcluded,
            },
          },
        ])

        await forceRefresh()
      } finally {
        removeOptimisticToggle()
      }
    },
    [allForm781AEntries, annotation, rolodexPatch, forceRefresh]
  )

  return {
    inProgressForms,
    isFormChecked,
    toggleFormSelection,
    isLoading: !hasEverLoaded,
  }
}
